import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, Eye, EyeOff, Plus, Truck, BadgeCheck, MapPin, KeyRound, Trash2, MoreVertical } from "lucide-react";
import { Logo } from "../components/Logo";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, FullLoader, ProviderNotice, Spinner } from "../components/common";

const BG = {
  virtual: "radial-gradient(ellipse at 68% 34%, rgba(168,85,247,0.55), transparent 55%), radial-gradient(circle at 18% 82%, rgba(124,58,237,0.4), transparent 52%), linear-gradient(135deg,#120826 0%,#1b0e3a 42%,#07040f 100%)",
  physical: "radial-gradient(ellipse at 68% 34%, rgba(150,70,240,0.5), transparent 55%), radial-gradient(circle at 18% 82%, rgba(109,40,217,0.38), transparent 52%), linear-gradient(135deg,#0f0920 0%,#180d34 46%,#060309 100%)",
};

function BrandMark({ logoData }) {
  if (logoData) return <img src={logoData} alt="logo" className="h-6 max-w-[120px] object-contain drop-shadow" />;
  return <Logo size={22} light />;
}

function Chip() {
  return (
    <div className="w-11 h-8 rounded-md bg-gradient-to-br from-[#E6E6ED] via-[#B8B8C6] to-[#8E8EA0] relative overflow-hidden shadow-inner ring-1 ring-white/30">
      <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-[1px] opacity-30">
        {Array.from({ length: 9 }).map((_, i) => <div key={i} className="bg-[#6b6b7a]" />)}
      </div>
    </div>
  );
}

function Contactless() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" className="text-white/90 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]" aria-hidden="true">
      <path d="M7 5.5a9 9 0 0 1 0 13" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" opacity="0.55" />
      <path d="M10.6 4a13 13 0 0 1 0 16" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" opacity="0.8" />
      <path d="M14.2 2.6a17 17 0 0 1 0 18.8" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
    </svg>
  );
}

function Galaxy() {
  return (
    <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
      <div className="absolute w-[72%] h-[55%] left-[22%] top-[16%] rotate-[-18deg] rounded-full blur-2xl"
        style={{ background: "radial-gradient(ellipse at center, rgba(205,125,255,0.5), transparent 65%)" }} />
    </div>
  );
}

export const DEFAULT_DESIGN = {
  bg_from: "#120826", bg_via: "#1b0e3a", bg_to: "#07040f", glow: "#cd7dff", logo_data: "",
  brand: { show: true, x: 0, y: 0 }, cardType: { show: true, x: 0, y: 0, text: "" },
  chip: { show: true, x: 0, y: 0 }, contactless: { show: true, x: 0, y: 0 },
  number: { show: true, x: 0, y: 0, color: "#ffffff" }, holder: { show: true, x: 0, y: 0, color: "#ffffff" },
  network: { show: true, x: 0, y: 0, label: "DEBIT", brand: "VISA" },
};

export function CardVisual({ card, frozen, unlocking, sensitive, flipped, onFlip, design }) {
  const { user } = useAuth();
  const d = { ...DEFAULT_DESIGN, ...(design || {}) };
  const fmtNum = (n) => (n ? n.replace(/(.{4})/g, "$1 ").trim() : null);
  const revealed = !!sensitive?.number;
  const exp = `${String(card.exp_month).padStart(2, "0")}/${String(card.exp_year).slice(-2)}`;
  const faceBase = `card-face overflow-hidden rounded-3xl text-white shadow-[0_0_0_1.5px_rgba(168,85,247,0.55),0_24px_60px_rgba(147,51,234,0.5)] ${frozen ? "card-frozen" : ""}`;
  const bg = `radial-gradient(ellipse at 68% 34%, ${d.glow}8c, transparent 55%), radial-gradient(circle at 18% 82%, ${d.glow}66, transparent 52%), linear-gradient(135deg, ${d.bg_from} 0%, ${d.bg_via} 42%, ${d.bg_to} 100%)`;
  const off = (e) => ({ transform: `translate(${e?.x || 0}px, ${e?.y || 0}px)` });
  return (
    <div className="relative">
      <div className="card-flip">
        <div className={`card-flip-inner ${flipped ? "is-flipped" : ""}`} onClick={onFlip} data-testid={`card-flip-${card.card_id}`}>
          {/* FRONT */}
          <div className={`${faceBase} p-6`} style={{ background: bg, opacity: flipped ? 0 : 1, transition: "opacity 0s linear .3s" }} data-testid={`card-visual-${card.card_id}`}>
            <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
              <div className="absolute w-[72%] h-[55%] left-[22%] top-[16%] rotate-[-18deg] rounded-full blur-2xl"
                style={{ background: `radial-gradient(ellipse at center, ${d.glow}, transparent 65%)`, opacity: 0.5 }} />
            </div>
            {/* neon inner edge */}
            <div className="absolute inset-[3px] rounded-[20px] pointer-events-none" style={{ boxShadow: "inset 0 0 0 1px rgba(201,166,255,0.35)" }} />
            <div className="relative flex flex-col h-full justify-between">
              <div className="flex justify-between items-start">
                {d.cardType.show ? (
                  <div className="flex items-center gap-2" style={off(d.cardType)}>
                    <Lock size={15} className="text-white/80 drop-shadow" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/85">
                      {d.cardType.text || (card.type === "physical" ? "Physical" : "Virtual")}
                    </span>
                  </div>
                ) : <span />}
                {d.brand.show && <div style={off(d.brand)}><BrandMark logoData={d.logo_data} /></div>}
              </div>

              <div className="mt-1">
                <div className="flex items-center gap-3">
                  {d.chip.show && <div style={off(d.chip)}><Chip /></div>}
                  {d.contactless.show && <div style={off(d.contactless)}><Contactless /></div>}
                </div>
              </div>

              <div className="flex justify-between items-end">
                <span />
                {d.network.show && (
                  <div className="text-right leading-none" style={off(d.network)}>
                    {d.network.label && <div className="text-[10px] font-semibold tracking-[0.3em] text-white/85 mb-0.5">{d.network.label}</div>}
                    <div className="font-display font-black italic text-3xl tracking-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]">{d.network.brand}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* BACK */}
          <div className={`${faceBase} card-face-back`} style={{ background: bg, opacity: flipped ? 1 : 0, transition: "opacity 0s linear .3s" }} data-testid={`card-back-${card.card_id}`}>
            <Galaxy />
            <div className="relative flex flex-col h-full">
              <div className="h-10 bg-black/85 mt-5" />
              <div className="px-6 mt-4">
                <div className="text-[9px] uppercase tracking-widest text-white/60 mb-1">Authorized signature</div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-8 rounded bg-white/85" />
                  <div className="px-3 h-8 min-w-[54px] rounded bg-white text-neutral-800 font-mono text-sm flex items-center justify-center" data-testid={`card-cvc-${card.card_id}`}>
                    {revealed && sensitive.cvc ? sensitive.cvc : "•••"}
                  </div>
                </div>
              </div>
              <div className="px-6 mt-auto mb-5">
                <div className="font-mono tracking-[0.16em] text-sm text-white/90">
                  {revealed ? fmtNum(sensitive.number) : `•••• •••• •••• ${card.last4}`}
                </div>
                <div className="flex justify-between items-end mt-1.5">
                  <span className="text-sm font-medium uppercase" data-testid={`card-holder-${card.card_id}`}>{card.holder_name || user?.full_name || "JACK CASH MEMBER"}</span>
                  <span className="text-xs font-mono text-white/80">EXP {exp}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {frozen && (
        <div className="frost-overlay absolute inset-0 rounded-3xl grid place-items-center z-10 backdrop-blur-[2px]" data-testid={`card-frozen-overlay-${card.card_id}`} style={{ background: "rgba(20,12,45,0.45)" }}>
          <div className="relative flex flex-col items-center">
            <div className="lock-badge grid place-items-center w-16 h-16 rounded-full bg-destructive text-white shadow-[0_8px_28px_rgba(239,68,68,0.6)] ring-4 ring-white/15"><Lock size={28} /></div>
            <div className="mt-3 font-display font-bold tracking-[0.35em] text-destructive text-xl drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">LOCKED</div>
          </div>
        </div>
      )}
      {unlocking && !frozen && (
        <div className="unlock-overlay absolute inset-0 rounded-3xl grid place-items-center z-10 backdrop-blur-[2px]" data-testid={`card-unlock-overlay-${card.card_id}`} style={{ background: "rgba(5,40,20,0.42)" }}>
          <div className="relative flex flex-col items-center">
            <div className="lock-badge grid place-items-center w-16 h-16 rounded-full bg-success text-white shadow-[0_8px_28px_rgba(34,197,94,0.6)] ring-4 ring-white/15"><Unlock size={28} /></div>
            <div className="mt-3 font-display font-bold tracking-[0.35em] text-success text-xl drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">UNLOCKED</div>
          </div>
        </div>
      )}
    </div>
  );
}

function ShippingModal({ open, onClose, onSubmit, busy, saved, defaultName }) {
  const [f, setF] = useState({ name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  if (!open) return null;
  const inp = "w-full h-12 px-4 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm";
  const useSaved = () => setF({
    name: defaultName || "", line1: saved.line1 || "", line2: saved.line2 || "",
    city: saved.city || "", state: saved.state || "", postal_code: saved.zip || "", country: "US",
  });
  const submit = () => {
    if (!f.name || !f.line1 || !f.city || !f.state || !f.postal_code) return toast.error("Fill all required fields");
    onSubmit(f);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-card rounded-t-3xl p-6 pb-10 animate-fade-up max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="shipping-modal">
        <div className="w-10 h-1 rounded-full bg-border mx-auto mb-5" />
        <h3 className="font-display text-xl font-semibold flex items-center gap-2"><Truck size={20} className="text-primary" /> Ship your physical card</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-4">Enter the US address where we should mail your card.</p>
        {saved && saved.line1 && (
          <button data-testid="ship-use-saved" onClick={useSaved}
            className="w-full flex items-center gap-3 p-3 mb-4 rounded-xl bg-primary/5 border border-primary/20 text-left transition-colors hover:bg-primary/10">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-primary/10 text-primary shrink-0"><MapPin size={16} /></span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">Use my saved address</div>
              <div className="text-xs text-muted-foreground truncate">{saved.line1}, {saved.city}, {saved.state} {saved.zip}</div>
            </div>
          </button>
        )}
        <div className="space-y-3">
          <input data-testid="ship-name" placeholder="Full name" value={f.name} onChange={set("name")} className={inp} />
          <input data-testid="ship-line1" placeholder="Street address" value={f.line1} onChange={set("line1")} className={inp} />
          <input data-testid="ship-line2" placeholder="Apt, suite (optional)" value={f.line2} onChange={set("line2")} className={inp} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="ship-city" placeholder="City" value={f.city} onChange={set("city")} className={inp} />
            <input data-testid="ship-state" placeholder="State (e.g. CA)" value={f.state} onChange={set("state")} className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="ship-zip" placeholder="ZIP code" value={f.postal_code} onChange={set("postal_code")} className={inp} />
            <input placeholder="Country" value="US" disabled className={inp + " opacity-60"} />
          </div>
        </div>
        <button data-testid="ship-submit" onClick={submit} disabled={busy}
          className="w-full h-13 mt-5 py-3 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Spinner className="w-5 h-5" /> : "Order card"}
        </button>
      </div>
    </div>
  );
}

function PinModal({ open, onClose, onSubmit, busy, isChange }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [current, setCurrent] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setPin(""); setConfirm(""); setCurrent(""); setErr(""); } }, [open]);
  if (!open) return null;
  const only = (v) => v.replace(/\D/g, "").slice(0, 4);
  const submit = () => {
    if (isChange && current.length !== 4) return setErr("Enter your current 4-digit PIN");
    if (pin.length !== 4) return setErr("PIN must be exactly 4 digits");
    if (pin !== confirm) return setErr("PINs do not match");
    onSubmit({ pin, current_pin: isChange ? current : undefined });
  };
  const inp = "w-full h-12 px-4 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-center tracking-[0.6em] font-mono text-lg";
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-card rounded-t-3xl p-6 pb-10 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="card-pin-modal">
        <div className="w-10 h-1 rounded-full bg-border mx-auto mb-5" />
        <h3 className="font-display text-xl font-semibold flex items-center gap-2"><KeyRound size={20} className="text-primary" /> {isChange ? "Change card PIN" : "Set card PIN"}</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-4">Choose a 4-digit PIN for your physical card, used at ATMs and in stores.</p>
        <div className="space-y-3">
          {isChange && (
            <input data-testid="card-pin-current" type="password" inputMode="numeric" placeholder="Current PIN"
              value={current} onChange={(e) => setCurrent(only(e.target.value))} className={inp} />
          )}
          <input data-testid="card-pin-new" type="password" inputMode="numeric" placeholder="New 4-digit PIN"
            value={pin} onChange={(e) => setPin(only(e.target.value))} className={inp} />
          <input data-testid="card-pin-confirm" type="password" inputMode="numeric" placeholder="Confirm PIN"
            value={confirm} onChange={(e) => setConfirm(only(e.target.value))} className={inp} />
          {err && <p className="text-xs text-destructive" data-testid="card-pin-error">{err}</p>}
        </div>
        <button data-testid="card-pin-submit" onClick={submit} disabled={busy}
          className="w-full h-12 mt-5 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Spinner className="w-5 h-5" /> : "Save PIN"}
        </button>
      </div>
    </div>
  );
}

export default function Cards() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmCard, setConfirmCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [issuing, setIssuing] = useState("");
  const [setupMsg, setSetupMsg] = useState(null);
  const [sensitive, setSensitive] = useState(null);
  const [shipOpen, setShipOpen] = useState(false);
  const [flippedId, setFlippedId] = useState(null);
  const [design, setDesign] = useState(null);
  const [unlockingId, setUnlockingId] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinCard, setPinCard] = useState(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [physPinSet, setPhysPinSet] = useState(false);
  const [physPinMode, setPhysPinMode] = useState(false);
  const [menuCardId, setMenuCardId] = useState(null);

  const load = async () => {
    try { const res = await api.get("/cards"); setData(res.data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/cards/design").then((r) => setDesign(r.data)).catch(() => {});
    api.get("/cards/physical-pin/status").then((r) => setPhysPinSet(!!r.data.pin_set)).catch(() => {});
  }, []);

  const cards = data?.cards || [];
  const virtualCount = cards.filter((c) => c.type === "virtual").length;
  const physical = cards.find((c) => c.type === "physical");

  const createCard = async (type, shipping) => {
    setIssuing(type); setSetupMsg(null);
    try {
      await api.post("/cards/issue", { type, shipping });
      toast.success(type === "physical" ? "Physical card ordered" : "Virtual card created");
      setShipOpen(false);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code && d?.message) setSetupMsg(d.message);
      else toast.error(errMsg(e));
    } finally { setIssuing(""); }
  };

  const activate = async (card) => {
    setBusy(true);
    try {
      await api.post(`/cards/${card.card_id}/activate`);
      toast.success("Card activated — now set your PIN");
      await load();
      if (!physPinSet) { setPhysPinMode(true); setPinCard(null); setPinOpen(true); }
    }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const toggleFreeze = async () => {
    const card = confirmCard;
    if (!card) return;
    const next = !card.frozen;
    setData((prev) => ({ ...prev, cards: prev.cards.map((c) => c.card_id === card.card_id ? { ...c, frozen: next } : c) }));
    setConfirmCard(null);
    if (!next) { setUnlockingId(card.card_id); setTimeout(() => setUnlockingId(null), 950); }
    setBusy(true);
    try { await api.post("/cards/freeze", { frozen: next, card_id: card.card_id }); toast.success(next ? "Card locked" : "Card unlocked"); await load(); }
    catch (e) {
      setData((prev) => ({ ...prev, cards: prev.cards.map((c) => c.card_id === card.card_id ? { ...c, frozen: !next } : c) }));
      setUnlockingId(null); toast.error(errMsg(e));
    }
    finally { setBusy(false); }
  };

  const openPhysicalPin = () => { setPhysPinMode(true); setPinCard(null); setPinOpen(true); };

  const savePin = async ({ pin, current_pin }) => {
    setPinBusy(true);
    try {
      if (physPinMode) {
        await api.post("/cards/physical-pin", { pin, current_pin });
        setPhysPinSet(true);
      } else {
        await api.post(`/cards/${pinCard.card_id}/pin`, { pin, current_pin });
      }
      toast.success("Card PIN saved");
      setPinOpen(false);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPinBusy(false); }
  };

  const showNumber = async (card) => {
    if (sensitive?.card_id === card.card_id) { setSensitive(null); return; }
    try { const { data } = await api.get(`/cards/${card.card_id}/sensitive`); setSensitive({ ...data, card_id: card.card_id }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const removeCard = async () => {
    const card = removeTarget;
    if (!card) return;
    setBusy(true);
    try { await api.delete(`/cards/${card.card_id}`); setRemoveTarget(null); toast.success("Card removed"); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (loading || !data) return <FullLoader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Cards" subtitle="Virtual & physical debit cards" />

      {cards.length === 0 && (
        <ProviderNotice name="Debit Card" label={setupMsg ? "SETUP REQUIRED" : data.status_label}
          message={setupMsg || "Create a virtual card to pay online, or order a physical card to use anywhere. Real cards are issued through Stripe Issuing once your Stripe account is claimed and Issuing is activated. No fake card numbers are generated."} />
      )}

      <div className="space-y-6">
        {cards.map((card) => (
          <div key={card.card_id}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold" data-testid={`card-label-${card.card_id}`}>{card.type === "physical" ? "Physical card" : "Virtual card"}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground font-mono">•••• {card.last4}</span>
                <div className="relative">
                  <button data-testid={`card-menu-btn-${card.card_id}`} onClick={() => setMenuCardId(menuCardId === card.card_id ? null : card.card_id)}
                    className="grid place-items-center w-8 h-8 rounded-full text-muted-foreground hover:bg-secondary transition-colors">
                    <MoreVertical size={18} />
                  </button>
                  {menuCardId === card.card_id && (
                    <>
                      <div className="fixed inset-0 z-20" onClick={() => setMenuCardId(null)} />
                      <div className="absolute right-0 top-9 z-30 w-44 rounded-2xl bg-card border border-border shadow-xl py-1.5 animate-fade-up" data-testid={`card-menu-${card.card_id}`}>
                        <button data-testid={`remove-card-btn-${card.card_id}`} onClick={() => { setMenuCardId(null); setRemoveTarget(card); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/5 transition-colors">
                          <Trash2 size={16} /> Remove card
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <CardVisual card={card} frozen={card.frozen} unlocking={unlockingId === card.card_id} design={design} sensitive={sensitive?.card_id === card.card_id ? sensitive : null}
              flipped={flippedId === card.card_id}
              onFlip={() => setFlippedId(flippedId === card.card_id ? null : card.card_id)} />
            <p className="text-center text-xs text-muted-foreground mt-2" data-testid={`card-flip-hint-${card.card_id}`}>Tap the card to flip</p>

            {card.type === "physical" && card.status !== "active" && (
              <button data-testid="activate-card-btn" onClick={() => activate(card)} disabled={busy}
                className="w-full h-12 mt-3 rounded-2xl bg-success text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Spinner className="w-5 h-5" /> : <><BadgeCheck size={18} /> Activate card (when it arrives)</>}
              </button>
            )}

            {card.type === "physical" && card.status === "active" && (
              <button data-testid="set-card-pin-btn" onClick={openPhysicalPin}
                className="w-full h-12 mt-3 rounded-2xl bg-card border border-border font-semibold flex items-center justify-center gap-2 transition-transform duration-200 active:scale-95">
                <KeyRound size={18} className="text-primary" /> {physPinSet ? "Change card PIN" : "Set card PIN"}
              </button>
            )}
            {card.type === "physical" && card.status !== "active" && (
              <p className="text-center text-[11px] text-muted-foreground px-4 mt-2" data-testid="pin-after-activate-hint">
                You'll set your 4-digit ATM &amp; in-store PIN right after you activate your card.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3 mt-3">
              <button data-testid={`card-show-btn-${card.card_id}`} onClick={() => showNumber(card)}
                className="flex flex-col items-center gap-2 py-4 rounded-2xl bg-card border border-border transition-transform duration-200 active:scale-95">
                {sensitive?.card_id === card.card_id ? <EyeOff size={20} className="text-primary" /> : <Eye size={20} className="text-primary" />}
                <span className="text-xs font-semibold">{sensitive?.card_id === card.card_id ? "Hide details" : "Show details"}</span>
              </button>
              <button data-testid={`card-freeze-btn-${card.card_id}`} onClick={() => setConfirmCard(card)}
                className={`flex flex-col items-center gap-2 py-4 rounded-2xl border transition-transform duration-200 active:scale-95 ${card.frozen ? "bg-destructive/10 border-destructive/20 text-destructive" : "bg-card border-border"}`}>
                {card.frozen ? <Unlock size={20} className="text-destructive" /> : <Lock size={20} className="text-primary" />}
                <span className="text-xs font-semibold">{card.frozen ? "Unlock" : "Lock"}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add cards */}
      <div className="space-y-3 mt-6">
        {virtualCount < 3 && (
          <button data-testid="create-virtual-btn" onClick={() => createCard("virtual")} disabled={!!issuing}
            className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 shadow-[0_12px_32px_rgba(75,35,204,0.3)] disabled:opacity-60">
            {issuing === "virtual" ? <Spinner className="w-5 h-5" /> : <><Plus size={18} /> {virtualCount === 0 ? "Create virtual card" : "Add another virtual card"}</>}
          </button>
        )}
        {virtualCount >= 3 && (
          <p className="text-center text-xs text-muted-foreground" data-testid="virtual-limit-note">You've reached the maximum of 3 virtual cards.</p>
        )}
        {!physical && (
          <button data-testid="order-physical-btn" onClick={() => setShipOpen(true)} disabled={!!issuing}
            className="w-full h-14 rounded-2xl bg-card border border-border font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            <Truck size={18} className="text-primary" /> Order physical card
          </button>
        )}
      </div>

      <ShippingModal open={shipOpen} onClose={() => setShipOpen(false)} busy={issuing === "physical"}
        saved={user?.address} defaultName={user?.full_name}
        onSubmit={(f) => createCard("physical", f)} />

      <PinModal open={pinOpen} onClose={() => { setPinOpen(false); setPhysPinMode(false); }} busy={pinBusy}
        isChange={physPinMode ? physPinSet : !!pinCard?.card_pin_set} onSubmit={savePin} />

      {removeTarget && (
        <div data-testid="remove-confirm-modal" className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={() => setRemoveTarget(null)}>
          <div className="w-full max-w-md bg-card rounded-t-3xl p-6 pb-10 animate-fade-up text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-border mx-auto mb-6" />
            <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-destructive/10 text-destructive"><Trash2 size={30} /></div>
            <h3 className="font-display text-xl font-semibold mt-4">Remove this card?</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">
              Your {removeTarget.type} card ending in {removeTarget.last4} will be permanently removed. This cannot be undone.
            </p>
            <div className="flex gap-3 mt-6">
              <button data-testid="remove-cancel-btn" onClick={() => setRemoveTarget(null)} className="flex-1 py-3.5 rounded-2xl bg-secondary font-semibold">Cancel</button>
              <button data-testid="remove-confirm-btn" onClick={removeCard} disabled={busy}
                className="flex-1 py-3.5 rounded-2xl bg-destructive text-white font-semibold flex items-center justify-center">
                {busy ? <Spinner className="w-5 h-5" /> : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmCard && (
        <div data-testid="freeze-confirm-modal" className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={() => setConfirmCard(null)}>
          <div className="w-full max-w-md bg-card rounded-t-3xl p-6 pb-10 animate-fade-up text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-border mx-auto mb-6" />
            <div className={`mx-auto grid place-items-center w-16 h-16 rounded-full ${confirmCard.frozen ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
              {confirmCard.frozen ? <Unlock size={30} /> : <Lock size={30} />}
            </div>
            <h3 className="font-display text-xl font-semibold mt-4">{confirmCard.frozen ? "Unlock card?" : "Lock card?"}</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">
              {confirmCard.frozen ? "This card will be reactivated and can be used again." : "This card will be locked. No transactions will go through until you unlock it."}
            </p>
            <div className="flex gap-3 mt-6">
              <button data-testid="freeze-cancel-btn" onClick={() => setConfirmCard(null)} className="flex-1 py-3.5 rounded-2xl bg-secondary font-semibold">Cancel</button>
              <button data-testid="freeze-confirm-btn" onClick={toggleFreeze} disabled={busy}
                className={`flex-1 py-3.5 rounded-2xl text-white font-semibold flex items-center justify-center ${confirmCard.frozen ? "bg-success" : "bg-destructive"}`}>
                {busy ? <Spinner className="w-5 h-5" /> : (confirmCard.frozen ? "Unlock" : "Lock")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
